import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Clipboard, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';

interface UserMessageProps {
  text: string;
  timestamp?: Date;
}

export function UserMessage({ text, timestamp }: UserMessageProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const [copied, setCopied] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsExpanded(false);
  }, [text]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el || isExpanded) return;

    const checkOverflow = () => {
      setNeedsTruncation(el.scrollHeight > el.clientHeight + 1);
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [text, isExpanded]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  return (
    <div className="flex justify-end w-full min-w-0 animate-fade-in mb-2">
      <div className="flex flex-col items-end max-w-[85vw] sm:max-w-2xl min-w-0">
        <div className="bg-accent text-accent-foreground py-2 px-3 rounded-2xl rounded-br-none w-fit min-w-0 overflow-hidden">
          <div className="relative">
            <div
              ref={contentRef}
              className={cn(
                'whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-sm leading-relaxed',
                !isExpanded && 'max-h-[120px] overflow-hidden'
              )}
            >
              {text}
            </div>
            {!isExpanded && needsTruncation && (
              <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-accent to-transparent pointer-events-none" />
            )}
          </div>
          {needsTruncation && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-1 flex items-center gap-0.5 text-xs font-medium text-accent-foreground/70 hover:text-accent-foreground transition-colors"
            >
              {isExpanded ? 'Show less' : 'Show more'}
              <ChevronDown
                className={cn('w-3.5 h-3.5 transition-transform', isExpanded && 'rotate-180')}
              />
            </button>
          )}
        </div>

        {/* Copy Button & Timestamp Row */}
        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground/85 select-none px-1">
          {timestamp && (
            <span>
              {new Date(timestamp).toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })}
            </span>
          )}
          <button
            onClick={handleCopy}
            className="hover:text-foreground p-0.5 transition-colors duration-150 rounded"
            title="Copy message"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 text-green-500 animate-in fade-in zoom-in-75 duration-200" />
            ) : (
              <Copy className="w-3.5 h-3.5 text-muted-foreground/60 hover:text-foreground transition-colors" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
